/**
 * Frontend Türkçe karakter ve büyük-küçük harf duyarsız arama utilities
 */

/**
 * Kelime başlarını büyük, kalanını küçük yapar (tr-TR).
 * Örn: "ham ekru" / "HAM EKRU" → "Ham Ekru"
 */
export const toTitleCaseTr = (input) => {
  if (input === null || input === undefined) return input;
  const lower = String(input).toLocaleLowerCase('tr-TR').trim().replace(/\s+/g, ' ');
  if (!lower) return '';
  return lower.split(' ').map((part) => part
    .split(/([-'/])/)
    .map((seg) => {
      if (/^[-'/]$/.test(seg) || seg.length === 0) return seg;
      return seg.charAt(0).toLocaleUpperCase('tr-TR') + seg.slice(1);
    })
    .join('')).join(' ');
};

// Türkçe karakterleri normalize etmek için mapping
// Tüm büyük harfler ve Türkçe karakterler için eksiksiz mapping
const turkishCharMap = {
  // Türkçe karakterler
  'ı': 'i', 'İ': 'i', 'I': 'i', 'i': 'i',
  'ğ': 'g', 'Ğ': 'g', 'G': 'g', 'g': 'g',
  'ü': 'u', 'Ü': 'u', 'U': 'u', 'u': 'u',
  'ş': 's', 'Ş': 's', 'S': 's', 's': 's',
  'ö': 'o', 'Ö': 'o', 'O': 'o', 'o': 'o',
  'ç': 'c', 'Ç': 'c', 'C': 'c', 'c': 'c',
  // İngilizce büyük harfler
  'A': 'a', 'B': 'b', 'D': 'd', 'E': 'e', 'F': 'f',
  'H': 'h', 'J': 'j', 'K': 'k', 'L': 'l', 'M': 'm',
  'N': 'n', 'P': 'p', 'Q': 'q', 'R': 'r', 'T': 't',
  'V': 'v', 'W': 'w', 'X': 'x', 'Y': 'y', 'Z': 'z'
};

/**
 * Metni Türkçe karakter ve büyük-küçük harf duyarsız hale getirir
 * @param {string} text - Dönüştürülecek metin
 * @returns {string} - Normalize edilmiş metin
 */
export const normalizeTextForSearch = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // Önce karakter karakter mapping yap, sonra kalan büyük harfleri küçült
  const mapped = text
    .split('')
    .map(char => {
      // Önce mapping'de var mı kontrol et
      if (turkishCharMap[char] !== undefined) {
        return turkishCharMap[char];
      }
      // Mapping'de yoksa, büyük harf ise küçült (locale-independent)
      const charCode = char.charCodeAt(0);
      if (charCode >= 65 && charCode <= 90) { // A-Z
        return String.fromCharCode(charCode + 32); // a-z
      }
      // Diğer karakterler olduğu gibi kalır
      return char;
    })
    .join('');

  // Aksan/şapka gibi birleştirici karakterleri kaldır (örn: â -> a)
  // Böylece "bilal" araması "Bilâl" gibi kayıtları da bulur.
  return mapped
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Metin içinde Türkçe karakter ve büyük-küçük harf duyarsız arama yapar
 * @param {string} text - Aranacak metin
 * @param {string} searchTerm - Arama terimi
 * @returns {boolean} - Arama terimi metin içinde var mı
 */
export const turkishIncludes = (text, searchTerm) => {
  if (!text || !searchTerm) {
    return false;
  }
  
  const normalizedText = normalizeTextForSearch(text);
  const normalizedSearch = normalizeTextForSearch(searchTerm);
  
  return normalizedText.includes(normalizedSearch);
};

/**
 * Stok kartlarını Türkçe karakter duyarsız filtreler
 * @param {Array} stockCards - Stok kartları listesi
 * @param {string} searchTerm - Arama terimi
 * @returns {Array} - Filtrelenmiş stok kartları
 */
export const filterStockCardsTurkish = (stockCards, searchTerm) => {
  if (!searchTerm || !stockCards) {
    return stockCards;
  }
  
  return stockCards.filter(stockCard => {
    return turkishIncludes(stockCard.name, searchTerm) || 
           turkishIncludes(stockCard.stockCode, searchTerm);
  });
};

/**
 * Çok kelimeli arama için stok kartı ve kalemlerini kontrol eder
 * Her kelime stok kartı adı/kodunda VEYA kalemlerde (renk, desen, varyant, parti no) bulunmalı
 * @param {Object} stockCard - Stok kartı
 * @param {Array} items - Stok kalemleri
 * @param {string} searchTerm - Arama terimi (birden fazla kelime olabilir)
 * @returns {boolean} - Tüm kelimeler eşleşiyorsa true
 */
export const matchesMultiWordSearch = (stockCard, items, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return true;
  }
  
  const searchWords = searchTerm.trim().split(/\s+/).filter(word => word.length > 0);
  if (searchWords.length === 0) {
    return true;
  }
  
  // Her kelime için kontrol et
  return searchWords.every(word => {
    // Stok kartı adında veya kodunda var mı?
    const matchesCard = turkishIncludes(stockCard.name, word) || 
                        turkishIncludes(stockCard.stockCode, word);
    
    if (matchesCard) {
      return true;
    }
    
    // Kalemlerde var mı? (renk, desen, varyant, parti no)
    if (items && items.length > 0) {
      return items.some(item => {
        const color = item.color || '';
        const pattern = item.pattern || '';
        const variant = item.variant || '';
        const batch = item.batchNumber || '';
        
        return turkishIncludes(color, word) ||
               turkishIncludes(pattern, word) ||
               turkishIncludes(variant, word) ||
               turkishIncludes(batch, word);
      });
    }
    
    return false;
  });
};
